import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Plus, Search, ArrowUpDown, ArrowUp, ArrowDown, Building2, Library } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';

type SortField = 'name' | 'memberCount' | 'createdAt';

export default function Organizations() {
  const navigate = useNavigate();
  const { t } = useTranslation('nav');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { data: orgs, isLoading, error } = trpc.platform.listOrganizations.useQuery();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filtered = (orgs ?? [])
    .filter(org => {
      if (!search) return true;
      const q = search.toLowerCase();
      return org.name.toLowerCase().includes(q)
        || org.slug.toLowerCase().includes(q)
        || (org.shopifyDomain ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      if (sortField === 'name') return dir * a.name.localeCompare(b.name);
      if (sortField === 'memberCount') return dir * (a.memberCount - b.memberCount);
      if (sortField === 'createdAt') return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return 0;
    });

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Stores</h2>
            <p className="text-muted-foreground text-sm mt-0.5">
              Manage all stores on the platform
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/platform/catalog')}>
              <Library className="mr-2 h-4 w-4" />
              {t('supplierCatalog')}
            </Button>
            <Button onClick={() => navigate('/platform/stores/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Add Store
            </Button>
          </div>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stores..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('name')}>
                    Store <SortIcon field="name" />
                  </button>
                </TableHead>
                <TableHead>Shopify Domain</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('memberCount')}>
                    Members <SortIcon field="memberCount" />
                  </button>
                </TableHead>
                <TableHead>Sync Status</TableHead>
                <TableHead>
                  <button className="flex items-center font-medium" onClick={() => handleSort('createdAt')}>
                    Created <SortIcon field="createdAt" />
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                    <span className="text-sm text-muted-foreground">Loading stores...</span>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-destructive">
                    Failed to load stores
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <Building2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      {search ? 'No stores match your search' : 'No stores yet'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(org => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/platform/stores/${org.id}`)}
                  >
                    <TableCell>
                      <div>
                        <span className="font-medium">{org.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{org.slug}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {org.shopifyDomain || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {org.planType ?? 'standard'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {org.isActive ? (
                        <Badge variant="default" className="bg-green-600 text-white text-xs">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>{org.memberCount}</TableCell>
                    <TableCell>
                      {org.syncStatus ? (
                        org.syncStatus.lastError ? (
                          <Badge variant="destructive" className="text-xs">Error</Badge>
                        ) : org.syncStatus.lastSyncedAt ? (
                          <span className="text-xs text-muted-foreground">
                            {new Date(org.syncStatus.lastSyncedAt).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Never</span>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground">{'-'}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

      </main>
    </div>
  );
}
